import { useRef, useState, useCallback } from 'react';

/**
 * useClickLock - Prevents double clicks and duplicate actions
 * 
 * CORE INTERACTION SYSTEM - Apply to ALL critical buttons
 * 
 * Architecture:
 * - useRef for instant lock (no re-render delay)
 * - useState for loading UI
 * - No fixed timeout - unlocks after actual API response
 * 
 * Rules:
 * 1. Execute instantly
 * 2. Lock on first click
 * 3. Show loading state
 * 4. Prevent duplicate calls
 * 5. Recover on error
 * 
 * @returns {object} { loading, execute }
 * 
 * Usage:
 * const { loading, execute } = useClickLock();
 * 
 * <Button 
 *   disabled={loading} 
 *   onClick={() => execute(async () => {
 *     await sendMessage({ text, requestId: generateRequestId() });
 *   })}
 * >
 *   {loading ? <Loader2 className="animate-spin" /> : 'Send'}
 * </Button>
 */
export function useClickLock() {
  const isProcessingRef = useRef(false);
  const [loading, setLoading] = useState(false);

  const execute = useCallback(async (fn) => {
    // Instant lock - prevents any duplicate clicks
    if (isProcessingRef.current) return;

    isProcessingRef.current = true;
    setLoading(true);

    try {
      await fn();
    } catch (err) {
      console.error('[useClickLock] Error:', err);
    } finally {
      isProcessingRef.current = false;
      setLoading(false);
    }
  }, []);

  return { loading, execute };
}

/**
 * generateRequestId - Creates unique request ID for backend deduplication
 * 
 * Send with every critical API call:
 * await api.post('/action', { data, requestId: generateRequestId() });
 * 
 * Backend must store and ignore duplicate requestId
 */
export function generateRequestId() {
  return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

export default useClickLock;
